import {
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  Building2,
  ClipboardCheck,
  CreditCard,
  ShieldAlert,
  ShieldCheck,
  Users,
  Wallet,
} from "lucide-react";
import { Link } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { getGlobalStats } from "@/lib/api/admin";

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  hint?: string;
  to?: string;
}

function StatCard({ label, value, icon, color, hint, to }: StatCardProps) {
  const card = (
    <Card padding="lg" radius="xl" style={{ height: "100%", cursor: to ? "pointer" : undefined }}>
      <Group gap="md" wrap="nowrap" align="flex-start">
        <ThemeIcon size={48} radius="xl" variant="light" color={color}>
          {icon}
        </ThemeIcon>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: "0.04em" }}>
            {label}
          </Text>
          <Text size="2rem" fw={800} lh={1}>
            {value}
          </Text>
          {hint && (
            <Text size="xs" c="dimmed">
              {hint}
            </Text>
          )}
        </Stack>
      </Group>
    </Card>
  );
  return to ? <Link to={to} style={{ textDecoration: "none", color: "inherit" }}>{card}</Link> : card;
}

export function AdminDashboardPage() {
  const { data } = useQuery({ queryKey: ["admin-stats"], queryFn: getGlobalStats });

  return (
    <Stack gap="lg">
      <PageHeader
        title="Super Admin Panel"
        subtitle="Stratix Med Pro — markaziy boshqaruv"
      />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          label="Klinikalar"
          value={data?.clinic_count ?? "—"}
          icon={<Building2 size={22} strokeWidth={2} />}
          color="brand"
          to="/admin/clinics"
        />
        <StatCard
          label="Faol"
          value={data?.active_clinic_count ?? "—"}
          icon={<ShieldCheck size={22} strokeWidth={2} />}
          color="green"
        />
        <StatCard
          label="Trial"
          value={data?.trial_clinic_count ?? "—"}
          icon={<AlertCircle size={22} strokeWidth={2} />}
          color="yellow"
        />
        <StatCard
          label="Bloklangan"
          value={data?.blocked_clinic_count ?? "—"}
          icon={<ShieldAlert size={22} strokeWidth={2} />}
          color="red"
        />
      </SimpleGrid>

      <Title order={4} fw={700} mt="md">
        Tibbiy faollik
      </Title>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          label="Foydalanuvchilar"
          value={data?.total_users ?? "—"}
          icon={<Users size={22} strokeWidth={2} />}
          color="violet"
        />
        <StatCard
          label="Bemorlar"
          value={data?.total_patients ?? "—"}
          icon={<Activity size={22} strokeWidth={2} />}
          color="blue"
        />
        <StatCard
          label="Ko'riklar"
          value={data?.total_examinations ?? "—"}
          icon={<ClipboardCheck size={22} strokeWidth={2} />}
          color="indigo"
        />
        <StatCard
          label="Yakunlangan"
          value={data?.total_completed ?? "—"}
          icon={<ShieldCheck size={22} strokeWidth={2} />}
          color="green"
        />
      </SimpleGrid>

      <Title order={4} fw={700} mt="md">
        Moliya
      </Title>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <StatCard
          label="To'langan"
          value={(data?.total_revenue_paid ?? 0).toLocaleString() + " UZS"}
          icon={<Wallet size={22} strokeWidth={2} />}
          color="green"
          hint="barcha to'langan invoyslar"
        />
        <StatCard
          label="Qarz"
          value={(data?.total_revenue_outstanding ?? 0).toLocaleString() + " UZS"}
          icon={<CreditCard size={22} strokeWidth={2} />}
          color="orange"
          hint="kutayotgan/ qisman to'langan"
        />
      </SimpleGrid>
    </Stack>
  );
}
